/**
 * The sun's shadows of a storey's furniture -- what players build on the floor (`sprite2`: tables,
 * stools, chests, barrels, sacks, beds standing up, doors) -- for MapRenderingManager to draw and publish
 * with the rest (foliage_shadow.ts). Furniture is drawn a cell at a time, so each cell's own art casts:
 * stood up from the bottom row of its piece and sheared along the sun, a pixel moved as far as it stands
 * above that row, like an upright entity tile (foliage_shadow_geometry.ts uprightShadowParts).
 */
import { NumberedTileMeta, TileSize } from "game-common/models"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import type { ShadowPolygon } from "./building_shadow_geometry"
import { contentTextureGeneration } from "./content_textures"
import { PlantSilhouette, plantSilhouette, ShadowStamp } from "./foliage_shadow_geometry"
import { compositeArtPixels } from "./sprites/composite_art_pixels"

/** Pieces that lie in or on the floor, or are drawn by their own sprites: no upright shadow. */
const FLAT = new Set(["stairs-up", "stairs-down", "floor-opening", "hatch-wood"])

/** A piece's cells, by the cell its parts all name as their parent. */
export interface FurniturePiece {
    /** The cell every part names as its parent. */
    parent: { x: number; y: number }
    cells: { x: number; y: number; tile: string }[]
    left: number; top: number; right: number; bottom: number
}

/** Every piece of furniture on a storey's `sprite2` layer. */
export const furniturePieces = (provider: TileMapMetaProvider): FurniturePiece[] => {
    const pieces = new Map<string, FurniturePiece>()
    provider.resolveTileMapFromLayerId("sprite2")?.forEach((column, x) => column?.forEach((tile, y) => {
        if (!tile || tile === "0" || provider.isHiddenTile(tile)) return
        if (FLAT.has(provider.resolveTileMeta(tile)?.entityTileId)) return
        if (!provider.shouldDrawTileAt(x, y, "sprite2", provider.resolveTileMapFromLayerId("sprite2"))) return
        const parent = provider.getMapDeltaAt(x, y, "sprite2")?.context?.parent ?? { x, y }
        const key = `${parent.x},${parent.y}`
        const piece = pieces.get(key) ?? { parent: { x: parent.x, y: parent.y }, cells: [], left: x, top: y, right: x, bottom: y }
        piece.cells.push({ x, y, tile })
        piece.left = Math.min(piece.left, x); piece.right = Math.max(piece.right, x)
        piece.top = Math.min(piece.top, y); piece.bottom = Math.max(piece.bottom, y)
        pieces.set(key, piece)
    }))
    return [...pieces.values()]
}

// One silhouette per tile art and content generation (a Lab publish can repaint it).
const silhouettes = new Map<string, PlantSilhouette | null>()
let artGeneration = -1

const cellArt = (provider: TileMapMetaProvider, tile: string): PlantSilhouette | null | undefined => {
    const generation = contentTextureGeneration()
    if (generation !== artGeneration) {
        silhouettes.clear()
        artGeneration = generation
    }
    const tileset = provider.resolveTileSet(tile)
    if (!tileset) return null
    const id = `${tileset.tilesetId}:${Number(tile) - tileset.firstGid}`
    if (silhouettes.has(id)) return silhouettes.get(id)
    const pixels = compositeArtPixels(tileset.tilesetId, tileset.width, [{ tileNumber: Number(tile) - tileset.firstGid + 1 } as NumberedTileMeta])
    if (!pixels) return undefined
    const art = plantSilhouette(id, pixels.data, pixels.width, pixels.height)
    silhouettes.set(id, art.rows.length ? art : null)
    return silhouettes.get(id)
}

export interface FurnitureShadows { stamps: ShadowStamp[]; feet: ShadowPolygon[]; pending: boolean }

/**
 * The furniture's shadows on this storey, in its pixels, and each piece's foot (for contact shade).
 * `indoors(x, y)`: whether a cell is out of the sun (under a roof or the floor above); a piece standing
 * there casts no sun shadow, only its contact shade. `pending` while some art could not be read yet.
 */
export const furnitureShadows = (provider: TileMapMetaProvider, sun: { x: number; y: number },
    indoors: (x: number, y: number) => boolean = () => false): FurnitureShadows => {
    const stamps: ShadowStamp[] = [], feet: ShadowPolygon[] = []
    let pending = false
    for (const piece of furniturePieces(provider)) {
        const arts = piece.cells.map(cell => ({ ...cell, art: cellArt(provider, cell.tile) }))
        if (arts.some(cell => cell.art === undefined)) { pending = true; continue }
        const drawn = arts.filter(cell => cell.art)
        if (!drawn.length) continue
        // The piece stands where its art ends: the lowest drawn row of any of its cells (a cell's art can
        // stop short of the cell's bottom edge, and a shadow from that edge hung below the legs).
        const base = Math.max(...drawn.map(({ y, art }) => y * TileSize + art.rows[art.rows.length - 1].y + 1))
        const x0 = piece.left * TileSize + 3, x1 = (piece.right + 1) * TileSize - 3, cy = base - 3
        feet.push([x0, cy - 3, x1, cy - 3, x1, cy + 3, x0, cy + 3])
        if (indoors(piece.parent.x, piece.parent.y)) continue
        for (const { x, y, art } of drawn) {
            const top = art.rows[0].y, bottom = art.rows[art.rows.length - 1].y + 1
            const lift = base - (y * TileSize + top)
            stamps.push({ art, y: top, height: bottom - top,
                matrix: [1, 0, -sun.x, -sun.y, x * TileSize + sun.x * lift, base + sun.y * lift] })
        }
    }
    return { stamps, feet, pending }
}
